import { ChangeDetectionStrategy, Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { take } from 'rxjs';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { BriefAiService } from '../../services/brief-ai.service';
import { ProductService } from '../../../../services/product/product.service';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { UgcProductResponseDto } from '../../../../common/general-types/products.dto';
import { BriefBasicsSection } from '../../sections/brief-basics-section/brief-basics-section';
import { ContentSection } from '../../sections/content-section/content-section';
import { CreativeSection } from '../../sections/creative-section/creative-section';
import { AudienceSection } from '../../sections/audience-section/audience-section';
import { ScreeningSection } from '../../sections/screening-section/screening-section';
import { ProductSection } from '../../sections/product-section/product-section';
import { CompensationSection } from '../../sections/compensation-section/compensation-section';
import { BbSummaryCard } from '../../ui/bb-summary-card/bb-summary-card';
import { BbAiDraftingLoader } from '../../ui/bb-ai-drafting-loader/bb-ai-drafting-loader';
import { ImageService } from '../../../../services/image/image.service';
import { GaService } from '../../../../services/ga/ga';
import type { CreateBriefRegularStepAction } from '../../../../services/ga/create-brief-regular.analytics';

type Stage = 'intake' | 'generating' | 'review';

@Component({
  selector: 'bb-ai-first-flow',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BriefBasicsSection, ContentSection, CreativeSection, AudienceSection, ScreeningSection, ProductSection, CompensationSection, BbSummaryCard, BbAiDraftingLoader],
  templateUrl: './ai-first-flow.html',
  styleUrl: './ai-first-flow.scss',
})
export class AiFirstFlow implements OnInit {
  readonly service = inject(CreateBriefService);
  readonly briefAi = inject(BriefAiService);
  private readonly productService = inject(ProductService);
  private readonly currentUserService = inject(CurrentUserService);
  public readonly imageService = inject(ImageService);
  private readonly gaService = inject(GaService);

  readonly launch = output<void>();
  readonly startOver = output<void>();

  readonly stage = signal<Stage>('intake');
  readonly prompt = signal<string>('');
  readonly products = signal<UgcProductResponseDto[]>([]);
  readonly aiBusy = signal<boolean>(false);
  readonly canLaunch = computed(() =>
    this.service.hasSelectedPricingModel() && (this.service.pieces()?.length ?? 0) > 0,
  );

  readonly examples = [
    'Show our app clearing a chaotic Monday inbox',
    "Relatable 'work smarter' demos for 25–44 professionals",
    'Authentic before/after of a real workflow',
  ];

  readonly examplesExtended: { name: string, description: string, prompt: string }[] = [
    { name: 'Showcase', description: 'Highlights the product and builds trust.', prompt: 'Instruct the creator to showcase the product and build trust.' },
    { name: 'Testimonial', description: 'A testimonial from a happy customer.', prompt: 'Instruct the creator to give a testimonial from a happy customer.' },
    { name: 'How-To', description: 'Shows how to use the product.', prompt: 'Instruct the creator to show how to use the product.' },
    { name: 'Unboxing', description: 'Unboxes the product and shows what is inside.', prompt: 'Instruct the creator to unbox the product and show what is inside.' },
    { name: 'Review', description: 'Reviews the product and gives an honest opinion.', prompt: 'Instruct the creator to review the product and give an honest opinion.' },
    { name: 'Comparison', description: 'Shows how the product compares to other products.', prompt: 'Instruct the creator to show how the product compares to other products.' },
    { name: 'Basic', description: 'Create a simple starter brief.', prompt: 'Create a simple starter brief considering the product and the campaign.' },
  ]

  ngOnInit(): void {
    // Resume into review if a draft already exists (e.g. duplicate prefill).
    if (this.service.basicInfoModel().briefTitle && this.service.creativeConcepts().length) {
      this.stage.set('review');
      this.trackStage('review', 'viewed');
    } else {
      this.trackStage('intake', 'viewed');
    }
    const brandId = this.currentUserService.focussedBrand?.id;
    if (brandId) {
      this.productService
        .getbusinessProducts(brandId)
        .pipe(take(1))
        .subscribe({ next: (response) => this.products.set((response as UgcProductResponseDto[]) ?? []) });
    }
  }

  async generate(): Promise<void> {
    if (this.aiBusy()) {
      return;
    }
    // Fall back to the first product if none chosen (mirrors the prototype).
    if (!this.service.selectedProduct() && this.products().length) {
      this.service.selectedProduct.set(this.products()[0]);
    }
    if (this.prompt().trim()) {
      this.service.description.set(this.prompt().trim());
    }

    this.aiBusy.set(true);
    this.trackStage('intake', 'completed');
    this.stage.set('generating');
    this.trackStage('generating', 'viewed');

    const angle = this.prompt().trim() || 'Create a strong starter brief considering the product and campaign.';
    await this.briefAi.draftBrief(angle);

    this.aiBusy.set(false);
    this.stage.set('review');
    this.trackStage('review', 'viewed');
  }

  async regenerateCreative(): Promise<void> {
    if (this.aiBusy()) {
      return;
    }
    this.aiBusy.set(true);
    try {
      await this.briefAi.draftBrief(this.prompt().trim() || 'Regenerate the creative direction for this brief.');
    } finally {
      this.aiBusy.set(false);
    }
  }

  readonly reviewSections = [
    { id: 'brief', title: 'Brief basics' },
    { id: 'content', title: 'Content & delivery' },
    { id: 'creative', title: 'Creative direction' },
    { id: 'audience', title: 'Audience & persona' },
    { id: 'screening', title: 'Screening & settings' },
    { id: 'product', title: 'Product' },
  ] as const;

  private trackStage(stage: Stage, action: CreateBriefRegularStepAction): void {
    this.gaService.trackRegularBriefStep('ai', stage, action);
  }
}
